import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import {
  SortableContext,
  useSortable,
  UseSortableArguments,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { ReactNode } from "react"

import { RowReordering } from "./types"

export { DragOverlay as SortableRowsDragOverlay } from "@dnd-kit/core"

export interface SortableRowsProviderProps {
  rowReordering?: RowReordering
  children: ReactNode
}

export function useSortableRow({
  id,
  disabled,
}: Pick<UseSortableArguments, "id" | "disabled">) {
  const {
    setNodeRef,
    attributes,
    listeners,
    isDragging,
    isOver,
    activeIndex,
    index,
  } = useSortable({ id, disabled })

  let dropIndicator: "above" | "below" | undefined
  if (isOver && !isDragging) {
    dropIndicator = activeIndex > index ? "above" : "below"
  }

  return {
    setNodeRef,
    isDragging,
    isOver,
    dropIndicator,
    dragHandleProps: { ...attributes, ...listeners },
  }
}

export function SortableRowsProvider({
  rowReordering,
  children,
}: SortableRowsProviderProps) {
  const sensors = useSensors(useSensor(PointerSensor))

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragStart={rowReordering?.onDragStart}
      onDragEnd={rowReordering?.onDragEnd}
      sensors={sensors}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext
        items={rowReordering?.rowKeys ?? []}
        strategy={verticalListSortingStrategy}
      >
        {children}
      </SortableContext>
    </DndContext>
  )
}
